import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, FileDownload, Close } from '@mui/icons-material';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import FiltroLocalArvore, { NoLocalArvore } from '../../components/Dashboard/FiltroLocalArvore';
import { formatarKg, formatarNumero } from '../../utils/formatters';
import { ordenarLinhas } from '../../utils/matrizOrdenacao';
import { CONDICOES_AMBIENTAIS, MOTIVOS_INTERRUPCAO, formatarDiaBR, hojeISO, rotuloDe } from './InterrupcaoForm';

type ModoValor = 'registros' | 'peso' | 'ambos';
type CampoOrdenacao = 'nome' | 'municipio' | 'total' | 'meses';

interface InatividadeMes { motivo: string; condicao: string | null; dias: number }
interface Celula { registros: number; kg: number; inatividade: InatividadeMes[]; diasInativos: number }
interface Linha {
  uid: string;
  nome: string;
  municipioUf: string | null;
  semRegistro: boolean;
  celulas: (Celula | null)[];
  total: { registros: number; kg: number };
  mesesAtivos: number;
  diasInativos: number;
}
interface DadosMatriz {
  meses: string[];
  linhas: Linha[];
  totais: { porMes: { registros: number; kg: number }[]; registros: number; kg: number };
  maximo: { registros: number; kg: number };
  periodo: { inicio: string; fim: string } | null;
  localNaoAplicaInatividade: boolean;
}

const ABREV_MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const doisDigitos = (n: number) => String(n).padStart(2, '0');

/** Padrão: últimos 12 meses (do dia 1 de 11 meses atrás até hoje = 12 colunas). */
const periodoPadrao = () => {
  const h = new Date();
  const ini = new Date(h.getFullYear(), h.getMonth() - 11, 1);
  return { inicio: `${ini.getFullYear()}-${doisDigitos(ini.getMonth() + 1)}-01`, fim: hojeISO() };
};

const textoMesCompleto = (mes: string) => `${ABREV_MES[Number(mes.slice(5, 7)) - 1]}/${mes.slice(0, 4)}`;

const textoValor = (registros: number, kg: number, modo: ModoValor) =>
  modo === 'registros' ? formatarNumero(registros, 0) : modo === 'peso' ? formatarKg(kg) : `${formatarNumero(registros, 0)} · ${formatarKg(kg)} kg`;

const descreverInatividade = (c: Celula) =>
  c.inatividade
    .map((i) => `${i.dias} ${i.dias === 1 ? 'dia' : 'dias'} — ${rotuloDe(MOTIVOS_INTERRUPCAO, i.motivo)}${i.condicao ? ` (${rotuloDe(CONDICOES_AMBIENTAIS, i.condicao)})` : ''}`)
    .join('; ');

const LARGURA_NOME = 200;
const LARGURA_MUNICIPIO = 140;

const HACHURA = 'repeating-linear-gradient(135deg, rgba(245,166,35,0.75) 0 3px, rgba(245,166,35,0) 3px 7px)';

/** Cor da célula, proporcional ao valor numa escala única (azul claro → azul escuro). */
const estiloValor = (valor: number, max: number): React.CSSProperties => {
  if (valor <= 0 || max <= 0) return {};
  const r = Math.min(1, valor / max);
  return { backgroundColor: `rgba(0, 150, 200, ${(0.1 + 0.8 * r).toFixed(2)})`, color: r > 0.55 ? '#fff' : '#12495e' };
};

const MatrizPescadores: React.FC = () => {
  const navigate = useNavigate();
  const padrao = useMemo(periodoPadrao, []);
  const [projetos, setProjetos] = useState<any[]>([]);
  const [arvore, setArvore] = useState<NoLocalArvore[]>([]);
  const [uidProjeto, setUidProjeto] = useState('');
  const [locais, setLocais] = useState<string[]>([]);
  const [inicio, setInicio] = useState(padrao.inicio);
  const [fim, setFim] = useState(padrao.fim);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [somenteValidados, setSomenteValidados] = useState(false);
  const [incluirSemRegistro, setIncluirSemRegistro] = useState(false);
  const [modo, setModo] = useState<ModoValor>('registros');
  const [ordenar, setOrdenar] = useState<CampoOrdenacao>('nome');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('asc');

  const [dados, setDados] = useState<DadosMatriz | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [celulaAberta, setCelulaAberta] = useState<{ linha: Linha; mes: string } | null>(null);

  useEffect(() => {
    api.get('/pesca/projetos').then((res) => setProjetos(res.data.data || [])).catch(() => setProjetos([]));
    api.get('/pesca/locais/arvore').then((res) => setArvore(res.data.data || [])).catch(() => setArvore([]));
  }, []);

  // busca por nome com atraso, para não consultar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 350);
    return () => clearTimeout(t);
  }, [busca]);

  const filtrosQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (inicio && fim) { q.set('dataInicio', inicio); q.set('dataFim', fim); }
    if (uidProjeto) q.set('uid_pesca_projeto', uidProjeto);
    if (locais.length) q.set('uid_pesca_local', locais.join(','));
    if (buscaAplicada) q.set('busca', buscaAplicada);
    if (somenteValidados) q.set('validacao', 'somente_validados');
    if (incluirSemRegistro) q.set('incluir_sem_registro', 'true');
    return q.toString();
  }, [inicio, fim, uidProjeto, locais, buscaAplicada, somenteValidados, incluirSemRegistro]);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    api.get(`/pesca/stats/matriz-pescadores?${filtrosQuery}`)
      .then((res) => { if (ativo) { setDados(res.data.data); setErro(null); } })
      .catch((e) => { if (ativo) { setErro(e?.response?.data?.message || 'Falha ao carregar a matriz.'); setDados(null); } })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [filtrosQuery]);

  const linhas = useMemo(() => (dados ? ordenarLinhas(dados.linhas, ordenar, direcao, modo) : []), [dados, ordenar, direcao, modo]);

  const alternarOrdem = (campo: CampoOrdenacao) => {
    if (ordenar === campo) setDirecao((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setOrdenar(campo); setDirecao(campo === 'nome' || campo === 'municipio' ? 'asc' : 'desc'); }
  };
  const seta = (campo: CampoOrdenacao) => (ordenar === campo ? (direcao === 'asc' ? ' ▲' : ' ▼') : '');

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const q = new URLSearchParams(filtrosQuery);
      q.set('valor', modo); q.set('ordenar', ordenar); q.set('direcao', direcao);
      const res = await api.get(`/pesca/stats/matriz-pescadores/exportar?${q.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `matriz-pescadores-${hojeISO().replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      let mensagem = 'Não foi possível exportar a matriz.';
      if (err?.response?.data instanceof Blob) {
        try { mensagem = JSON.parse(await err.response.data.text())?.message || mensagem; } catch { /* mantém a mensagem padrão */ }
      }
      alert(mensagem);
    } finally {
      setExportando(false);
    }
  }, [filtrosQuery, modo, ordenar, direcao]);

  const meses = dados?.meses ?? [];
  const mostrarAno = meses.length > 12;
  const maxValor = dados ? (modo === 'peso' ? dados.maximo.kg : dados.maximo.registros) : 0;
  const valorCelula = (c: Celula | null) => (c ? (modo === 'peso' ? c.kg : c.registros) : 0);

  const rotuloColuna = (mes: string, i: number) => {
    if (mostrarAno) return textoMesCompleto(mes);
    const anoMuda = i === 0 || mes.slice(0, 4) !== meses[i - 1].slice(0, 4);
    return (
      <>
        {ABREV_MES[Number(mes.slice(5, 7)) - 1]}
        <div style={{ fontSize: '0.62rem', fontWeight: 400, color: '#888', minHeight: 10 }}>{anoMuda ? mes.slice(0, 4) : ''}</div>
      </>
    );
  };

  const th: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 3, background: '#f1f6f8', padding: '6px 8px', fontSize: '0.78rem', textAlign: 'center', borderBottom: '1px solid #d8e2e6', whiteSpace: 'nowrap' };
  // Larguras fixas: a coluna Município/UF fica sticky logo à direita do nome (left = LARGURA_NOME).
  const colNome: React.CSSProperties = { position: 'sticky', left: 0, zIndex: 2, background: '#fff', textAlign: 'left', padding: '4px 10px', borderRight: '1px solid #d8e2e6', width: LARGURA_NOME, minWidth: LARGURA_NOME, maxWidth: LARGURA_NOME, boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' };
  const colMunicipio: React.CSSProperties = { ...colNome, left: LARGURA_NOME, width: LARGURA_MUNICIPIO, minWidth: LARGURA_MUNICIPIO, maxWidth: LARGURA_MUNICIPIO, fontSize: '0.8rem' };
  const td: React.CSSProperties = { padding: '4px 8px', textAlign: 'center', fontSize: '0.8rem', borderBottom: '1px solid #eef2f4', borderRight: '1px solid #f3f6f7', minWidth: modo === 'ambos' ? 92 : 56, whiteSpace: 'nowrap' };

  const campo: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' };
  const rotulo: React.CSSProperties = { display: 'block', fontSize: '0.72rem', color: '#666', marginBottom: 3 };
  const chip = (ativo: boolean): React.CSSProperties => ({
    padding: '4px 12px', borderRadius: 14, border: ativo ? '1px solid #00c3ff' : '1px solid #ddd', background: ativo ? '#eef9ff' : '#fff',
    color: ativo ? '#0a6c8f' : '#555', cursor: 'pointer', fontSize: '0.8rem', fontWeight: ativo ? 700 : 500,
  });

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Matriz de pescadores</h1>
        <div className={styles.actionsHeader}>
          <button className={styles.blueBtn} onClick={exportar} disabled={exportando || carregando || !dados || dados.linhas.length === 0} title="Exportar .xlsx">
            <FileDownload />
          </button>
          <button className={styles.blueBtn} onClick={() => navigate('/admin/pescadores')} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <div className={styles.sectionCard} style={{ padding: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={rotulo}>Projeto</label>
            <select style={{ ...campo, minWidth: 190 }} value={uidProjeto} onChange={(e) => setUidProjeto(e.target.value)}>
              <option value="">Todos os projetos</option>
              {projetos.map((p) => <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 220 }}>
            <label style={rotulo}>Local de pesca</label>
            <FiltroLocalArvore nos={arvore} selecionados={locais} onChange={setLocais} />
          </div>
          <div>
            <label style={rotulo}>De</label>
            <input type="date" style={campo} value={inicio} max={fim || undefined} onChange={(e) => setInicio(e.target.value)} />
          </div>
          <div>
            <label style={rotulo}>Até</label>
            <input type="date" style={campo} value={fim} min={inicio || undefined} onChange={(e) => setFim(e.target.value)} />
          </div>
          <button type="button" style={chip(false)} onClick={() => { setInicio(padrao.inicio); setFim(padrao.fim); }}>Últimos 12 meses</button>
          <div>
            <label style={rotulo}>Buscar pescador</label>
            <input style={{ ...campo, minWidth: 170 }} value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome..." />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', marginTop: 12 }}>
          <label style={{ fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={somenteValidados} onChange={(e) => setSomenteValidados(e.target.checked)} />
            Somente validados
          </label>
          <label style={{ fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={incluirSemRegistro} onChange={(e) => setIncluirSemRegistro(e.target.checked)} />
            Incluir pescadores sem registro no período
          </label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} role="group" aria-label="Valor exibido">
            <span style={{ fontSize: '0.78rem', color: '#666' }}>Valor:</span>
            <button type="button" style={chip(modo === 'registros')} onClick={() => setModo('registros')}>Registros</button>
            <button type="button" style={chip(modo === 'peso')} onClick={() => setModo('peso')}>Peso (kg)</button>
            <button type="button" style={chip(modo === 'ambos')} onClick={() => setModo('ambos')}>Ambos</button>
          </div>
        </div>
      </div>

      <div className={styles.sectionCard} style={{ marginTop: 10, padding: 0 }}>
        {erro && (
          <p role="alert" style={{ margin: 0, padding: '12px 16px', background: '#fff7e6', color: '#7a5a00', fontSize: '0.85rem' }}>{erro}</p>
        )}
        {!erro && !carregando && dados && dados.linhas.length === 0 && (
          <p style={{ margin: 0, padding: '24px 16px', color: '#999' }}>Nenhum pescador para os filtros atuais.</p>
        )}
        {!erro && dados && dados.linhas.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: '68vh', opacity: carregando ? 0.6 : 1 }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...colNome, zIndex: 5, top: 0, cursor: 'pointer', background: '#f1f6f8' }} onClick={() => alternarOrdem('nome')}>Pescador{seta('nome')}</th>
                  <th style={{ ...th, ...colMunicipio, zIndex: 5, top: 0, cursor: 'pointer', background: '#f1f6f8' }} onClick={() => alternarOrdem('municipio')}>Município/UF{seta('municipio')}</th>
                  {meses.map((m, i) => <th key={m} style={th}>{rotuloColuna(m, i)}</th>)}
                  <th style={{ ...th, cursor: 'pointer', borderLeft: '2px solid #d8e2e6' }} onClick={() => alternarOrdem('total')}>Total{seta('total')}</th>
                  <th style={{ ...th, cursor: 'pointer' }} onClick={() => alternarOrdem('meses')}>Meses ativos{seta('meses')}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.uid}>
                    <td style={colNome} title={l.nome}>
                      <a href={`/admin/pescadores/view/${l.uid}`} onClick={(e) => { e.preventDefault(); navigate(`/admin/pescadores/view/${l.uid}`); }} style={{ color: '#12495e', textDecoration: 'none', fontWeight: 600 }}>{l.nome}</a>
                      {l.semRegistro && <small style={{ marginLeft: 6, color: '#999' }}>sem registro</small>}
                    </td>
                    <td style={{ ...colMunicipio, color: l.municipioUf ? '#333' : '#aaa' }} title={l.municipioUf || 'Sem município/UF no cadastro'}>{l.municipioUf || 'n/d'}</td>
                    {l.celulas.map((c, i) => {
                      const inativo = !!c && c.diasInativos > 0;
                      const temRegistro = !!c && c.registros > 0;
                      const titulo = [
                        `${l.nome} · ${textoMesCompleto(meses[i])}`,
                        temRegistro ? `${c!.registros} ${c!.registros === 1 ? 'registro' : 'registros'} · ${formatarKg(c!.kg)} kg` : null,
                        inativo ? `Atividade interrompida: ${descreverInatividade(c!)} (${c!.diasInativos} ${c!.diasInativos === 1 ? 'dia' : 'dias'} no mês)` : null,
                        temRegistro ? 'Clique para ver os registros' : null,
                      ].filter(Boolean).join('\n');
                      return (
                        <td
                          key={meses[i]}
                          title={titulo}
                          onClick={temRegistro ? () => setCelulaAberta({ linha: l, mes: meses[i] }) : undefined}
                          style={{
                            ...td,
                            ...estiloValor(valorCelula(c), maxValor),
                            ...(inativo ? { backgroundImage: HACHURA, outline: '1px solid rgba(245,166,35,0.9)', outlineOffset: -1 } : {}),
                            ...(!temRegistro && !inativo ? { color: '#ccc' } : {}),
                            cursor: temRegistro ? 'pointer' : 'default',
                          }}
                        >
                          {temRegistro ? textoValor(c!.registros, c!.kg, modo) : '·'}
                        </td>
                      );
                    })}
                    <td style={{ ...td, fontWeight: 700, borderLeft: '2px solid #d8e2e6' }}>{textoValor(l.total.registros, l.total.kg, modo)}</td>
                    <td style={td}>{l.mesesAtivos}/{meses.length}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...colNome, position: 'sticky', bottom: 0, zIndex: 4, fontWeight: 700, background: '#f1f6f8', borderTop: '2px solid #d8e2e6' }}>Total</td>
                  <td style={{ ...colMunicipio, position: 'sticky', bottom: 0, zIndex: 4, background: '#f1f6f8', borderTop: '2px solid #d8e2e6' }} />
                  {dados.totais.porMes.map((t, i) => (
                    <td key={meses[i]} style={{ ...td, position: 'sticky', bottom: 0, zIndex: 3, background: '#f1f6f8', fontWeight: 700, borderTop: '2px solid #d8e2e6' }}>
                      {t.registros > 0 ? textoValor(t.registros, t.kg, modo) : '·'}
                    </td>
                  ))}
                  <td style={{ ...td, position: 'sticky', bottom: 0, zIndex: 3, background: '#e3eef2', fontWeight: 800, borderTop: '2px solid #d8e2e6', borderLeft: '2px solid #d8e2e6' }}>{textoValor(dados.totais.registros, dados.totais.kg, modo)}</td>
                  <td style={{ ...td, position: 'sticky', bottom: 0, zIndex: 3, background: '#f1f6f8', borderTop: '2px solid #d8e2e6' }} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {carregando && !dados && <p style={{ margin: 0, padding: '24px 16px', color: '#999' }}>Carregando...</p>}
      </div>

      {dados && dados.linhas.length > 0 && (
        <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#666', lineHeight: 1.6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 22px', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 70, height: 12, borderRadius: 3, background: 'linear-gradient(90deg, rgba(0,150,200,0.1), rgba(0,150,200,0.9))' }} />
              0 → {modo === 'peso' ? `${formatarKg(dados.maximo.kg)} kg` : `${formatarNumero(dados.maximo.registros, 0)} registros`} (escala única da tabela{modo === 'ambos' ? ', pela contagem de registros' : ''})
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 22, height: 14, borderRadius: 3, backgroundImage: HACHURA, outline: '1px solid rgba(245,166,35,0.9)', outlineOffset: -1 }} />
              Atividade interrompida no mês (período sem atividade; passe o mouse para ver motivo e dias)
            </span>
            <span><span style={{ color: '#bbb' }}>·</span> sem registro e sem interrupção no mês</span>
          </div>
          <div>
            Peso convertido (bruto ×1, eviscerado ×1,2, filé ×3); só registros de produção, mesmas regras do Dashboard.
            {' '}Meses ativos = meses com ao menos um registro. Um período em andamento conta até hoje.
            {dados.localNaoAplicaInatividade && <strong style={{ color: '#8a5a00' }}> O filtro de local não se aplica à marcação de atividade interrompida.</strong>}
          </div>
        </div>
      )}

      {celulaAberta && (
        <RegistrosDaCelula
          linha={celulaAberta.linha}
          mes={celulaAberta.mes}
          filtrosQuery={filtrosQuery}
          onClose={() => setCelulaAberta(null)}
          onAbrir={(uid) => navigate(`/admin/registros/view/${uid}`)}
        />
      )}
    </div>
  );
};

interface RegistrosDaCelulaProps {
  linha: Linha;
  mes: string;
  filtrosQuery: string;
  onClose: () => void;
  onAbrir: (uidRegistro: string) => void;
}

const RegistrosDaCelula: React.FC<RegistrosDaCelulaProps> = ({ linha, mes, filtrosQuery, onClose, onAbrir }) => {
  const [itens, setItens] = useState<{ uid_pesca_registro: string; data: string; descricao_local: string | null; kg: number }[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    const q = new URLSearchParams(filtrosQuery);
    q.set('uid_pesca_pescador', linha.uid);
    q.set('mes', mes);
    api.get(`/pesca/stats/matriz-pescadores/registros?${q.toString()}`)
      .then((res) => { if (ativo) setItens(res.data.data || []); })
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao carregar os registros.'); });
    return () => { ativo = false; };
  }, [linha.uid, mes, filtrosQuery]);

  const totalKg = (itens || []).reduce((a, i) => a + i.kg, 0);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: '92%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--green-700, #00b37e)' }}>{linha.nome} · {textoMesCompleto(mes)}</h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>
        {erro && <p style={{ color: '#c4283a' }}>{erro}</p>}
        {!erro && itens === null && <p style={{ color: '#999' }}>Carregando...</p>}
        {itens && itens.length === 0 && <p style={{ color: '#999' }}>Nenhum registro neste mês para os filtros atuais.</p>}
        {itens && itens.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#666', borderBottom: '1px solid #e3e8ea' }}>
                <th style={{ padding: '6px 4px' }}>Data</th>
                <th style={{ padding: '6px 4px' }}>Local</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Peso (kg)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.uid_pesca_registro} style={{ borderBottom: '1px solid #f0f3f4' }}>
                  <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatarDiaBR(i.data)}</td>
                  <td style={{ padding: '6px 4px' }}>{i.descricao_local || '-'}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 600 }}>{formatarKg(i.kg)}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                    <a href={`/admin/registros/view/${i.uid_pesca_registro}`} onClick={(e) => { e.preventDefault(); onAbrir(i.uid_pesca_registro); }} style={{ color: 'var(--light-blue-dark, #0a6c8f)', fontWeight: 600 }}>Abrir</a>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={2} style={{ padding: '8px 4px' }}>{itens.length} {itens.length === 1 ? 'registro' : 'registros'}</td>
                <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatarKg(totalKg)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
        <p style={{ margin: '12px 0 0', fontSize: '0.72rem', color: '#888' }}>Mesmos filtros da matriz (projeto, local, somente validados); peso convertido.</p>
      </div>
    </div>
  );
};

export default MatrizPescadores;
